/**
 * Admin → Pricing.
 *
 * One screen for everything the desk prices from. It replaces the three
 * separate admin pricing screens (DSCR Pricing / RTL Pricing / Pricing Config),
 * which split one job across three nav entries and three scroll positions.
 *
 * Three tabs, each a single scrolling column of section cards with an "on this
 * page" anchor rail:
 *
 *   dscr   → /admin/data/dscr-pricing/*      counterparty sheets: coupon ladder,
 *            LLPA stack, tier margins, guideline overlays, version history
 *   rtl    → /admin/data/rtl-pricing/*       the additive RTL/GUC rate model,
 *            with the live rate-preview rail
 *   config → /correspondents/admin/*         the lock desk's inputs: DSCR tier
 *            margins and the RTL base-rate sheet
 *
 * URL is `/admin/pricing/:tab/:cpId?` — the tab lives in the path so a section
 * of this screen stays linkable, and `cpId` keeps the DSCR counterparty deep
 * links that `/admin/dscr-pricing/:cpId` used to serve.
 *
 * Not included, deliberately: the coupon-ladder bulk-shift / lock flow, the
 * prepayment-penalty option table, the treasury-curve "rates & spread" pricer
 * and the pricing-arrangements editor. Those exist only in the descoped
 * digital_sizer port (`src/api/adminPricingApi.js`), whose every route 404s
 * against eastview-os. They belong here once the backend exposes them.
 */
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import PriceChangeOutlined from "@mui/icons-material/PriceChangeOutlined";

import { AdminScreenShell } from "../shared/AdminScreenShell";
import DscrPricingPane from "./DscrPricingPane";
import RtlPricingPane from "./RtlPricingPane";
import PricingConfigPane from "./PricingConfigPane";

// Labels mirror ADMIN_NAV_ITEMS' `subTabs` for Pricing — AdminLayout renders the
// switcher itself, this map only titles the page.
const TAB_LABELS = {
  dscr: "DSCR Pricing",
  rtl: "RTL & GUC Pricing",
  config: "Pricing Config",
};

const SUBTITLES = {
  dscr: "Versioned counterparty price sheets — coupon ladders, the LLPA stack, tier margins and the guideline overlays that gate rather than price. Margins and LLPAs are bond points (par = 100); rates never appear on this tab.",
  rtl: "The additive RTL / GUC rate model: base rate by product plus term, borrower-class, loan-size and leverage adjustments. Each section versions independently.",
  config:
    "The correspondent-facing numbers a rate lock is written from. Every save is audited and published to the pricing_updates topic.",
};

export default function AdminPricingScreen() {
  const { tab, cpId } = useParams();

  const active = useMemo(() => (TAB_LABELS[tab] ? tab : "dscr"), [tab]);

  return (
    <AdminScreenShell
      icon={<PriceChangeOutlined />}
      // The breadcrumb above already says "Admin / Pricing", so the page heading
      // names the tab you're on rather than repeating the section.
      title={TAB_LABELS[active]}
      subtitle={SUBTITLES[active]}
      // One long page — it scrolls in the dashboard's <main> rather than
      // nesting its own scrollbar inside it.
    >
      {active === "dscr" ? <DscrPricingPane cpId={cpId} /> : null}
      {active === "rtl" ? <RtlPricingPane /> : null}
      {active === "config" ? <PricingConfigPane /> : null}
    </AdminScreenShell>
  );
}
